import React, { useState, useEffect } from 'react';
import { tenantNurseAPI } from '../../../services/api/tenantNurseAPI';
import useEscKeyHandler from '../../../hooks/useEscKeyHandler';
import toast from 'react-hot-toast';
import { 
  Send, 
  Mail, 
  MessageSquare, 
  Search, 
  Filter,
  Clock,
  CheckCircle,
  XCircle,
  Phone,
  User,
  Calendar,
  AlertCircle
} from 'lucide-react';

const PatientCommunications = () => {
  const [patients, setPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendingLink, setSendingLink] = useState(false);

  useEffect(() => {
    loadPatients();
  }, []);

  useEffect(() => {
    filterPatients();
  }, [searchTerm, patients]);

  // Add Esc key handler for modal
  useEscKeyHandler(showSendModal, () => setShowSendModal(false));

  const loadPatients = async () => {
    try {
      setLoading(true);
      const response = await tenantNurseAPI.getPatients();
      setPatients(response.data);
    } catch (error) {
      console.error('Failed to load patients:', error);
      toast.error('Failed to load patients');
    } finally {
      setLoading(false);
    }
  };

  const filterPatients = () => {
    if (!searchTerm) {
      setFilteredPatients(patients);
    } else {
      const filtered = patients.filter(patient =>
        patient.alias?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        patient.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        patient.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredPatients(filtered);
    }
  };

  const handleSendLink = (patient) => {
    setSelectedPatient(patient);
    setShowSendModal(true);
  };

  const SendLinkModal = () => {
    const [selectedChannel, setSelectedChannel] = useState('email');
    const [customMessage, setCustomMessage] = useState('');

    const handleSend = async () => {
      try {
        setSendingLink(true);
        await tenantNurseAPI.sendRecordingLink(selectedPatient.id, selectedChannel, customMessage);
        toast.success(`Recording link sent via ${selectedChannel} to ${selectedPatient.alias}`);
        setShowSendModal(false);
        setSelectedPatient(null);
      } catch (error) {
        console.error('Failed to send recording link:', error);
        toast.error(`Failed to send recording link via ${selectedChannel}`);
      } finally {
        setSendingLink(false);
      }
    };

    if (!selectedPatient) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Send Recording Link</h2>
              <button
                onClick={() => setShowSendModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Patient Info */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-teal-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold">
                    {selectedPatient.alias?.split(' ').map(n => n[0]).join('') || 'P'}
                  </span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{selectedPatient.alias}</h3>
                  <p className="text-sm text-gray-600">{selectedPatient.email}</p>
                  {selectedPatient.phone && (
                    <p className="text-sm text-gray-600">{selectedPatient.phone}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Communication Channel */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Choose Communication Channel
              </label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => setSelectedChannel('email')}
                  className={`p-4 border-2 rounded-lg flex items-center space-x-3 transition-colors ${
                    selectedChannel === 'email'
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <Mail className="h-6 w-6" />
                  <div className="text-left">
                    <p className="font-medium">Email</p>
                    <p className="text-sm text-gray-600">Send to {selectedPatient.email}</p>
                  </div>
                </button>

                <button
                  onClick={() => setSelectedChannel('sms')}
                  className={`p-4 border-2 rounded-lg flex items-center space-x-3 transition-colors ${
                    selectedChannel === 'sms'
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  disabled={!selectedPatient.phone}
                >
                  <MessageSquare className="h-6 w-6" />
                  <div className="text-left">
                    <p className="font-medium">SMS</p>
                    <p className="text-sm text-gray-600">
                      {selectedPatient.phone ? `Send to ${selectedPatient.phone}` : 'No phone number'}
                    </p>
                  </div>
                </button>
              </div>
            </div>

            {/* Custom Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Custom Message (Optional)
              </label>
              <textarea
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder="Add a personal message to include with the recording link..."
              />
            </div>

            {/* Preview */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">Message Preview:</h4>
              <div className="text-sm text-blue-800">
                <p className="font-medium">Subject: Your Vital Signs Recording Request</p>
                <div className="mt-2 space-y-1">
                  <p>Dear {selectedPatient.alias},</p>
                  <p>Please click the link below to record your vital signs using our secure system:</p>
                  <p className="font-mono bg-white px-2 py-1 rounded text-blue-600">
                    [Recording Link - Valid for 10 minutes]
                  </p>
                  {customMessage && (
                    <>
                      <p className="mt-2 font-medium">Personal message:</p>
                      <p className="italic">"{customMessage}"</p>
                    </>
                  )}
                  <p className="mt-2">Best regards,<br />Your Healthcare Team</p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                onClick={() => setShowSendModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSend}
                disabled={sendingLink || (!selectedPatient.email && selectedChannel === 'email') || (!selectedPatient.phone && selectedChannel === 'sms')}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center space-x-2"
              >
                {sendingLink ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    <span>Send Recording Link</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Patient Communications</h1>
          <p className="text-gray-600">Send recording links and manage patient communications</p>
        </div>
        <div className="flex items-center space-x-2 text-sm text-gray-600">
          <Clock className="h-4 w-4" />
          <span>Links expire in 10 minutes</span>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Send className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Links Sent Today</p>
              <p className="text-xl font-bold text-gray-900">24</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <CheckCircle className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Completed</p>
              <p className="text-xl font-bold text-gray-900">18</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Clock className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Pending</p>
              <p className="text-xl font-bold text-gray-900">6</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Success Rate</p>
              <p className="text-xl font-bold text-gray-900">75%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search patients by name, email, or alias..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>
          <button className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
            <Filter className="h-4 w-4" />
            <span>Filter</span>
          </button>
        </div>
      </div>

      {/* Patient List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Patients ({filteredPatients.length})</h3>
        </div>
        
        <div className="divide-y divide-gray-200">
          {filteredPatients.map((patient) => (
            <div key={patient.id} className="p-6 hover:bg-gray-50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-teal-600 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold">
                      {patient.alias?.split(' ').map(n => n[0]).join('') || 'P'}
                    </span>
                  </div>
                  
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">{patient.alias}</h4>
                    <div className="flex items-center space-x-4 mt-1">
                      <div className="flex items-center space-x-1 text-sm text-gray-600">
                        <Mail className="h-4 w-4" />
                        <span>{patient.email}</span>
                      </div>
                      {patient.phone && (
                        <div className="flex items-center space-x-1 text-sm text-gray-600">
                          <Phone className="h-4 w-4" />
                          <span>{patient.phone}</span>
                        </div>
                      )}
                      <div className="flex items-center space-x-1 text-sm text-gray-600">
                        <Calendar className="h-4 w-4" />
                        <span>Joined {new Date(patient.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  {/* Latest Vital Status */}
                  {patient.latest_vital ? (
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">
                        Latest: {new Date(patient.latest_vital.created_at).toLocaleDateString()}
                      </p>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        patient.latest_vital.quality_flag === 'High' ? 'bg-green-100 text-green-800' :
                        patient.latest_vital.quality_flag === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {patient.latest_vital.quality_flag || 'Normal'}
                      </span>
                    </div>
                  ) : (
                    <div className="text-right">
                      <p className="text-sm text-gray-500">No recent vitals</p>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                        Pending
                      </span>
                    </div>
                  )}

                  {/* Send Link Button */}
                  <button
                    onClick={() => handleSendLink(patient)}
                    className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Send className="h-4 w-4" />
                    <span>Send Link</span>
                  </button>
                </div>
              </div>

              {/* Show concerning vitals alert */}
              {patient.latest_vital && patient.latest_vital.rhythm && 
               ['Irregular', 'Atrial Fibrillation'].includes(patient.latest_vital.rhythm) && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="h-4 w-4 text-red-600" />
                    <p className="text-sm font-medium text-red-800">
                      Concerning rhythm detected: {patient.latest_vital.rhythm}
                    </p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {filteredPatients.length === 0 && (
          <div className="text-center py-12">
            <Send className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No patients found</h3>
            <p className="text-gray-500">
              {searchTerm ? 'Try adjusting your search criteria' : 'No patients available for communication'}
            </p>
          </div>
        )}
      </div>

      {/* Send Link Modal */}
      {showSendModal && <SendLinkModal />}
    </div>
  );
};

export default PatientCommunications;